import React, { useCallback, useRef, useEffect } from "react";
import { useHome } from "../hooks/useHome";
import "../styles/Home.css";
import Header from "../components/Header";
import { FloatButton } from "../components/FloatButton";
import { Toast } from "../components/Toast";
import { Spinner } from "../components/Spinner";
import { thumbnail25, image25 } from "../assets/25";
import { thumbnail120, image120 } from "../assets/120";
import { thumbnail150, image150 } from "../assets/150";
import img150_1_sample from "../assets/150/01_sample.mp4";
import { GarallyCommon } from "../components/GarallyCommon";
import { Memo } from "../components/Memo";
import { Contact } from "../components/Contact";
import { shouldShowTips, setTipsLastShown } from "../utils/tipsStorage";
import { Form } from "../components/Form";

export const Home: React.FC = () => {
  const {
    loading,
    title,
    message,
    error,
    setIsSignupComplete,
    isSignupComplete,
    handleSignup,
    isOpen,
    count,
    date,
  } = useHome();

  const [showToast, setShowToast] = React.useState(false);
  const [backgroundImage, setBackgroundImage] = React.useState<string | null>(
    null
  );
  const [prevBackgroundImage, setPrevBackgroundImage] = React.useState<
    string | null
  >(null);
  const [fadeState, setFadeState] = React.useState<"idle" | "fading">("idle");
  const [showTips, setShowTips] = React.useState(false);

  React.useEffect(() => {
    if (isSignupComplete) {
      setShowToast(true);
      const timer = setTimeout(() => setShowToast(false), 15000);
      return () => clearTimeout(timer);
    }
  }, [isSignupComplete]);

  React.useEffect(() => {
    if (shouldShowTips()) {
      setShowTips(true);
    }
    setTipsLastShown();
  }, []);

  const handleCloseTips = () => {
    setShowTips(false);
  };

  // 各GarallyCommonのrefを管理
  const garallyRefs = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];

  // Memoセクションへのref
  const memoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      for (let i = 0; i < garallyRefs.length; i++) {
        const ref = garallyRefs[i].current;
        if (ref) {
          const rect = ref.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            let imagesArr = [
              Object.values(image25),
              Object.values(image120),
              Object.values(image150),
            ];
            const newImage = imagesArr[i][0] as string;
            if (newImage !== backgroundImage) {
              setPrevBackgroundImage(backgroundImage);
              setBackgroundImage(newImage);
              setFadeState("fading");
            }
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundImage]);

  // フェード終了後に前の画像を消す
  useEffect(() => {
    if (fadeState === "fading") {
      const timer = setTimeout(() => {
        setPrevBackgroundImage(null);
        setFadeState("idle");
      }, 600); // CSSのtransitionと合わせる
      return () => clearTimeout(timer);
    }
  }, [fadeState]);

  const handleGotoTerm = useCallback(() => {
    // オフセット -124px 追加
    const target = memoRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - 124;
    window.scrollTo({ top: targetY, behavior: "smooth" });
  }, []);

  return (
    <>
      {loading && <Spinner />}
      {showToast && <Toast title={title} body={message} />}
      <div className="home-container">
        {/* 背景画像を固定レイヤーで表示 */}
        {/* フェード用に2枚重ねる */}
        {prevBackgroundImage && fadeState === "fading" && (
          <div
            className="home-background home-background-fadeout"
            style={{ backgroundImage: `url(${prevBackgroundImage})` }}
          />
        )}
        {backgroundImage && (
          <div
            className={
              "home-background" +
              (fadeState === "fading" ? " home-background-fadein" : "")
            }
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )}
        <div className="home-texter">
          <div className="home-content">
            <div className="float-button">
              <FloatButton onPress={handleGotoTerm} />
            </div>
            <section>
              <Header
                isOpen={isOpen}
                count={count}
                date={date}
                onPressRegist={(e) => {
                  setIsSignupComplete(false);
                  setShowToast(false);
                  handleSignup(e);
                }}
                error={error}
                showTips={showTips}
                onCloseTips={handleCloseTips}
              />
            </section>
            <section>
              <div ref={garallyRefs[0]}>
                <GarallyCommon
                  plan="character only"
                  price="25"
                  color="#F44E72"
                  body={
                    "Only individual characters can be displayed in full view.\nIn the case of character sheets, the number of poses varies.\nUp to 5 poses are possible."
                  }
                  subtitle="per pose"
                  firstImageCount={5}
                  images={Object.values(thumbnail25)}
                  originalImages={Object.values(image25)}
                  headerIndex={0}
                />
              </div>
            </section>
            <section>
              <div ref={garallyRefs[1]}>
                <GarallyCommon
                  plan={"Illustration with\nbackground"}
                  price="120"
                  color="#2BD194"
                  body={`Illustrations with backgrounds.
                  Creation of two types of rough drafts, with feedback at each stage of the process.`}
                  firstImageCount={4}
                  images={Object.values(thumbnail120)}
                  originalImages={Object.values(image120)}
                  headerIndex={1}
                />
              </div>
            </section>
            <section>
              <div ref={garallyRefs[2]}>
                <GarallyCommon
                  plan={"Illustration with\nbackground + \n\nLayers"}
                  price="150"
                  color="#F44E72"
                  body={`Illustrations with backgrounds.
                  Creation of two rough drafts, with feedback provided at each stage of the process.
                  Layer data for Live2D, blinking animation.`}
                  images={Object.values(thumbnail150)}
                  originalImages={Object.values(image150)}
                  movies={[img150_1_sample]}
                  headerIndex={0}
                />
              </div>
            </section>
            <section>
              <div ref={memoRef}>
                <Memo />
              </div>
            </section>
            <section>
              <div>
                <Form
                  isOpen={isOpen}
                  date={date}
                  onPressRegist={(e) => {
                    setIsSignupComplete(false);
                    setShowToast(false);
                    handleSignup(e);
                  }}
                  error={error}
                  showTips={showTips}
                  onCloseTips={handleCloseTips}
                  variant="light"
                />
              </div>
            </section>
            <section>
              <Contact />
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
